import { DashboardRequestError } from "../aws/client";
import { confirmSessionEnded, type SessionProbe } from "./sessionProbe";

/**
 * Errors raised by the CRM's own Vercel proxies (never by Supabase directly).
 * The `name` check keeps the guard working across module boundaries where the
 * class identity may differ (test doubles, duplicated chunks).
 */
export function isDashboardRequestError(
  error: unknown,
): error is DashboardRequestError {
  if (error instanceof DashboardRequestError) return true;
  return (
    typeof error === "object" &&
    error !== null &&
    (error as { name?: unknown }).name === "DashboardRequestError" &&
    typeof (error as { status?: unknown }).status === "number"
  );
}

/**
 * Decides whether a dashboard-proxy error must end the CRM session.
 *
 * The proxies answer 401 when they could not vouch for the bearer, 403 when
 * the operator lacks a role (`administrator_required`) and 503 when Supabase
 * itself was unavailable. Only a 401 can mean the session is dead, and even
 * then the browser confirms with Supabase first, so a proxy that failed closed
 * on a Supabase timeout never signs the operator out mid-task.
 */
export async function dashboardErrorEndsSession(
  error: DashboardRequestError,
  probeSession: SessionProbe,
): Promise<boolean> {
  if (error.status !== 401) return false;
  return confirmSessionEnded(probeSession);
}
